<template>
  <section id="topics" class="section section-observed topic-section cms-layout-gallery">
    <p v-if="front.pageSubtitle" class="layout-subtitle">{{ front.pageSubtitle }}</p>

    <div v-if="front.activeBlocks.length" class="gallery-blocks">
      <BlockRenderer
        v-for="block in front.activeBlocks"
        :key="block.blockId || block.blockCode"
        :block="block"
      />
    </div>

    <div class="gallery-toolbar" v-if="galleryCategories.length > 1">
      <button
        type="button"
        :class="{ active: activeCategory === 'all' }"
        @click="selectCategory('all')"
      >
        全部
      </button>
      <button
        v-for="cat in galleryCategories"
        :key="cat"
        type="button"
        :class="{ active: activeCategory === cat }"
        @click="selectCategory(cat)"
      >
        {{ cat }}
      </button>
    </div>

    <div v-if="loading" class="muted-copy">正在加载...</div>
    <div v-else-if="loadError" class="muted-copy error-copy">
      加载相册失败：{{ loadError }}
    </div>
    <div v-else-if="galleryItems.length" class="gallery-grid">
      <figure
        v-for="photo in galleryItems"
        :key="photo.entryId"
        class="is-clickable"
        role="button"
        tabindex="0"
        @click="openGalleryPhoto(photo)"
        @keydown.enter.space.prevent="openGalleryPhoto(photo)"
      >
        <img
          v-if="photo.coverUrl"
          :src="front.resolvePublicDisplayUrl(photo.coverUrl)"
          :alt="photo.title"
        />
        <div v-else class="thumb-empty">无图</div>
        <figcaption>
          <strong>{{ photo.title }}</strong>
          <span v-if="photo.date">{{ photo.date }}</span>
          <span v-if="photo.category">{{ photo.category }}</span>
          <p v-if="photo.importantEvent" class="gallery-event-box">
            <b>重要事件</b>
            <span>{{ photo.importantEvent }}</span>
          </p>
        </figcaption>
      </figure>
    </div>
    <p v-else class="muted-copy">
      暂无相册照片，请在管理后台「历史相册」中维护。
    </p>

    <FrontPagination
      :page-num="pageNum"
      :page-size="pageSize"
      :total="total"
      @change="onPageChange"
    />

    <CmsEntryDetailPanel v-if="front.selectedCmsEntry" />
  </section>
</template>

<script>
import { listPublicCmsEntries, unwrapPublicPage, applyClientPageFallback } from '../../../api/paleo'
import { sortCmsByHistoryTime } from '../../../utils/cmsExtra'
import FrontPagination from '../../common/FrontPagination.vue'
import CmsEntryDetailPanel from '../CmsEntryDetailPanel.vue'
import BlockRenderer from '../blocks/BlockRenderer.vue'

export default {
  name: 'GalleryLayout',
  components: { CmsEntryDetailPanel, BlockRenderer, FrontPagination },
  inject: ['front'],
  data() {
    return {
      activeCategory: 'all',
      galleryCategories: [],
      galleryItems: [],
      pageNum: 1,
      pageSize: 5,
      total: 0,
      loading: false,
      loadSeq: 0,
      loadError: ''
    }
  },
  watch: {
    '$route.query.category': {
      immediate: true,
      handler(category) {
        this.activeCategory = category ? String(category) : 'all'
        this.pageNum = 1
        this.loadGalleryPage()
      }
    }
  },
  mounted() {
    this.loadGalleryCategories()
    this.loadGalleryPage()
  },
  methods: {
    async loadGalleryCategories() {
      try {
        const result = await listPublicCmsEntries({
          moduleCode: 'gallery',
          scope: 'society',
          pageNum: 1,
          pageSize: 200
        })
        const { rows } = unwrapPublicPage(result)
        const set = new Set()
        rows.forEach(item => {
          const mapped = this.front.mapCmsEntry(item)
          if (mapped && mapped.category) set.add(mapped.category)
        })
        this.galleryCategories = Array.from(set)
      } catch (error) {
        this.galleryCategories = []
      }
    },
    async loadGalleryPage() {
      const seq = ++this.loadSeq
      this.loading = true
      this.loadError = ''
      try {
        const result = await listPublicCmsEntries({
          moduleCode: 'gallery',
          scope: 'society',
          pageNum: 1,
          pageSize: 200
        })
        if (seq !== this.loadSeq) return
        let rows = unwrapPublicPage(result).rows.map(item => this.front.mapCmsEntry(item))
        if (this.activeCategory && this.activeCategory !== 'all') {
          rows = rows.filter(item => item.category === this.activeCategory)
        }
        rows = sortCmsByHistoryTime(
          rows,
          (this.front.siteConfig && this.front.siteConfig.galleryHistorySort) || 'asc'
        )
        const paged = applyClientPageFallback(rows, rows.length, this.pageNum, this.pageSize)
        this.galleryItems = paged.rows
        this.total = paged.total
      } catch (error) {
        if (seq !== this.loadSeq) return
        this.loadError = error && error.message ? error.message : '未知错误'
        this.galleryItems = []
        this.total = 0
      } finally {
        if (seq === this.loadSeq) this.loading = false
      }
    },
    onPageChange(page) {
      this.pageNum = page
      this.loadGalleryPage()
    },
    selectCategory(category) {
      this.activeCategory = category
      this.pageNum = 1
      const query = category && category !== 'all' ? { category } : {}
      if (JSON.stringify(this.$route.query) !== JSON.stringify(query)) {
        this.$router.replace({ path: '/gallery', query }).catch(() => {})
      }
      this.loadGalleryPage()
    },
    openGalleryPhoto(photo) {
      this.front.selectedCmsEntry = photo
    }
  }
}
</script>

<style scoped>
.layout-subtitle {
  margin: 0 0 20px;
  color: var(--subtle);
  line-height: 1.8;
}

.gallery-blocks {
  display: grid;
  gap: 24px;
  margin-bottom: 24px;
}

.gallery-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}

.gallery-toolbar button {
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 6px 14px;
  background: #fff;
  color: var(--ink);
  cursor: pointer;
  font: inherit;
  font-size: 13px;
}

.gallery-toolbar button.active,
.gallery-toolbar button:hover {
  border-color: var(--primary);
  background: var(--primary);
  color: #fff;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.gallery-grid figure {
  margin: 0;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: var(--front-radius);
  background: #fff;
  box-shadow: var(--shadow-soft);
}

.gallery-grid figure.is-clickable {
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.gallery-grid figure.is-clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 29, 54, 0.12);
}

.gallery-grid img,
.thumb-empty {
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  display: block;
}

.thumb-empty {
  display: grid;
  place-items: center;
  background: #f3f6fa;
  color: var(--subtle);
  font-size: 14px;
}

.gallery-grid figcaption {
  display: grid;
  gap: 4px;
  padding: 12px 14px;
}

.gallery-grid figcaption strong {
  color: var(--primary);
  font-size: 14px;
}

.gallery-grid figcaption span {
  color: var(--subtle);
  font-size: 12px;
}

.gallery-event-box {
  display: grid;
  gap: 4px;
  margin: 6px 0 0;
  border-left: 3px solid var(--gold);
  border-radius: 4px;
  padding: 8px 10px;
  background: #f8f5ee;
}

.gallery-event-box b {
  color: var(--primary);
  font-size: 12px;
}

.gallery-event-box span {
  display: -webkit-box;
  overflow: hidden;
  color: var(--ink);
  line-height: 1.6;
  white-space: pre-line;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
}

.muted-copy {
  margin: 0;
  color: var(--subtle);
  line-height: 1.7;
}

.error-copy {
  color: #c0392b;
}

@media (max-width: 1100px) {
  .gallery-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .gallery-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  .gallery-grid {
    grid-template-columns: 1fr;
  }
}
</style>
